
import { useCallback, useEffect, useRef } from 'react';
import { getScrollbarWidth } from '@/utils/dom-utils';

export const useVigilantScrollLock = () => {
  const observerRef = useRef<MutationObserver | null>(null);
  const isLockedRef = useRef(false);
  const scrollbarWidthRef = useRef<number>(0);

  const lockScroll = useCallback(() => {
    if (isLockedRef.current) return;

    // Calculate and cache scrollbar width
    scrollbarWidthRef.current = getScrollbarWidth();
    
    // Apply scroll lock styles
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidthRef.current}px`;
    
    isLockedRef.current = true;

    // Create and start the MutationObserver to vigilantly guard our styles
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const currentPaddingRight = document.body.style.paddingRight;
          const expectedPaddingRight = `${scrollbarWidthRef.current}px`;
          
          // If something has changed our padding-right, force it back
          if (currentPaddingRight !== expectedPaddingRight) {
            document.body.style.paddingRight = expectedPaddingRight;
          }
          
          // Also ensure overflow stays hidden
          if (document.body.style.overflow !== 'hidden') {
            document.body.style.overflow = 'hidden';
          }
        }
      });
    });

    // Start observing the body element for style attribute changes
    observerRef.current.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });
  }, []);

  const unlockScroll = useCallback(() => {
    if (!isLockedRef.current) return;

    // Disconnect the observer first to prevent interference
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Remove all inline styles to restore original state
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    
    isLockedRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { lockScroll, unlockScroll };
};
